import { Button, ButtonType, ButtonSize, IconName } from '@hills-design-system/components';
import { Card } from '../../../components/Card/Card';
import { Tooltip } from '../../../components/Tooltip';
import { useNavigate } from 'react-router-dom';
import { OrderSectionLayout } from './OrderSectionLayout';
import { useContentstackPage } from '../../../hooks/useContentstack';
import { CmsFeature } from '../../../types/contentstack';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';

interface FeatureSection {
  name: string;
  features: CmsFeature[];
}

interface DistributorCardData {
  title: string;
  serviceAreaTooltip: string;
  distributorAccountTooltip: string;
  bottomText: string;
  buttonLabel: string;
  orderUrl: string;
}

function parseFeature(feature: CmsFeature): DistributorCardData {
  const body = feature.body ?? '';
  const title = feature.heading ?? '';

  // First <p> = bottom info text
  const pMatch = body.match(/<p>([\s\S]*?)<\/p>/);
  const bottomText = pMatch?.[1]?.replace(/<[^>]+>/g, '').trim() ?? '';

  // Extract all <h5> tags
  const h5Matches = [...body.matchAll(/<h5>([\s\S]*?)<\/h5>/g)];
  // First <h5> = service area tooltip
  const serviceAreaTooltip = h5Matches[0]?.[1]?.replace(/<[^>]+>/g, '').trim() ?? '';
  // Second <h5> = distributor account tooltip (has bold + note)
  // Keep HTML to preserve <strong> and <br> formatting
  const distributorAccountTooltip = h5Matches[1]?.[1]?.trim() ?? '';

  const buttonLabel = feature.buttons?.[0]?.text ?? '';
  const orderUrl = feature.buttons?.[0]?.url ?? '';

  return { title, serviceAreaTooltip, distributorAccountTooltip, bottomText, buttonLabel, orderUrl };
}

const DistributorCard = ({
  title,
  serviceAreaTooltip,
  distributorAccountTooltip,
  bottomText,
  buttonLabel,
  orderUrl,
}: DistributorCardData) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Card
      padding="large"
      radius="large"
      customBackground="bg-primary"
      className="flex flex-col justify-between border-none min-h-[200px] overflow-visible"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <h3 className="text-heading-200 font-bold text-white leading-heading tracking-[-3%]">
            {title}
          </h3>
          <Tooltip
            align="left"
            size={20}
            textClasses="text-white"
            icon={IconName.InfoFilled}
            content={
              <div className="w-[280px] rounded-200 p-3">
                <p>{serviceAreaTooltip}</p>
              </div>
            }
          />
        </div>
        <button
          onClick={() => navigate('/help?tab=contact-us')}
          className="flex items-center gap-1 text-white font-medium text-detail-90 cursor-pointer bg-transparent border-none p-0"
        >
          {t(TranslationKeyEnum.ContactInfo)}
          <span className="text-white">&rarr;</span>
        </button>
      </div>

      <div className="flex items-end justify-between mt-4">
        <div className="flex items-center gap-2 text-white">
          <Tooltip
            align="top"
            size={16}
            textClasses="text-white"
            icon={IconName.InfoFilled}
            content={
              <div
                className="w-[280px] p-3 [&_strong]:font-bold [&_br]:block"
                dangerouslySetInnerHTML={{ __html: distributorAccountTooltip }}
              />
            }
          />
          <span className="text-detail-90 font-medium leading-detail text-white">
            {bottomText}
          </span>
        </div>
        <Button
          label={buttonLabel || t(TranslationKeyEnum.StartNewOrder)}
          type={ButtonType.Primary}
          size={ButtonSize.Small}
          icon={IconName.ExternalLink}
          inverse
          data-id="cta"
          onClick={() => window.open(orderUrl, '_blank')}
        />
      </div>
    </Card>
  );
};

export const CanadianOrderSection = () => {
  const { data: pageData, loading } = useContentstackPage('ca-order-section');

  const featureSection = pageData?.components?.[0]
    ? ((pageData.components[0] as Record<string, unknown>).feature_section as FeatureSection)
    : null;

  const features = featureSection?.features ?? [];
  const subtitle = featureSection?.name ?? '';

  if (loading) {
    return (
      <OrderSectionLayout subtitle={subtitle}>
        <Card
          padding="large"
          radius="large"
          customBackground="bg-gray-50"
          height="h-[200px]"
          className="flex justify-center items-center"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </Card>
      </OrderSectionLayout>
    );
  }

  return (
    <OrderSectionLayout subtitle={subtitle}>
      <div className="flex flex-col gap-4">
        {features.map((feature) => {
          const data = parseFeature(feature);
          return <DistributorCard key={feature._metadata?.uid} {...data} />;
        })}
      </div>
    </OrderSectionLayout>
  );
};
